/* eslint-disable @typescript-eslint/naming-convention */
import { Injectable, inject } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { map, catchError } from 'rxjs/operators';

import { AuthUtils } from 'app/core/auth/auth.utils';
import { HttpClient } from '@angular/common/http';
import { RoleService } from 'app/core/auth/role.service';
import { UserService } from 'app/core/user/user.service';
import { Router } from '@angular/router';
import { environment } from 'environments/environment';

@Injectable({providedIn: 'root'})
export class AuthService
{
    private _authenticated: boolean = false;
    private _httpClient = inject(HttpClient);
    private _userService = inject(UserService);
    private _roleService = inject(RoleService);
    private _router = inject(Router);

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors - User Data from localStorage
    // -----------------------------------------------------------------------------------------------------

    set accessToken(token: string) {
        localStorage.setItem('accessToken', token);
    }

    get accessToken(): string {
        return localStorage.getItem('accessToken') ?? '';
    }

    get userId(): string {
        return localStorage.getItem('user-id') ?? '';
    }

    get userName(): string {
        return localStorage.getItem('user-name') ?? '';
    }

    get userSurnames(): string {
        return localStorage.getItem('user-surnames') ?? '';
    }

    get userEmail(): string {
        return localStorage.getItem('user-email') ?? '';
    }

    get userPhone(): string {
        return localStorage.getItem('user-phone') ?? '';
    }

    get tkExpires(): string {
        return localStorage.getItem('tkExpires') ?? '';
    }

    get userAvatar(): string {
        return localStorage.getItem('user-avatar') ?? '';
    }

    get userType(): string {
        return localStorage.getItem('user-type') ?? '';
    }

    get userStatus(): string {
        return localStorage.getItem('user-status') ?? '';
    }

    get tkRefresh(): string {
        return localStorage.getItem('tkRefresh') ?? '';
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Sign in with OAuth password grant
     *
     * @param credentials
     */
    signIn(credentials: { email: string; password: string }): Observable<any>
    {
        // Throw error, if the user is already logged in
        if (this._authenticated) {
            return throwError(() => 'User is already logged in.');
        }

        const body = {
            client_id: '1',
            grant_type: 'password',
            client_secret: environment.cs,
            username: credentials.email,
            password: credentials.password,
        };

        return this._httpClient.post(environment.urlBase + 'oauth/token', body).pipe(
            map((response: any) => {
                this.manageToken(response);
                this._authenticated = true;
                return response;
            })
        );
    }

    /**
     * Sign in using the refresh token
     */
    signInUsingToken(): Observable<boolean>
    {
        const refreshToken = localStorage.getItem('tkRefresh');
        if (!refreshToken) {
            return of(false);
        }

        const body = {
            client_id: '1',
            grant_type: 'refresh_token',
            client_secret: environment.cs,
            refresh_token: refreshToken,
        };

        return this._httpClient.post(environment.urlBase + 'oauth/token', body).pipe(
            map((result: any) => {
                this.manageToken(result);
                this._authenticated = true;
                return true;
            }),
            catchError(() => {
                this.signOut();
                return of(false);
            })
        );
    }

    /**
     * Sign out
     */
    signOut(): Observable<any>
    {
        // Remove user data from local storage
        // D60: la cache del rol se invalida aqui. RoleService.clear() es quien
        // borra `user-type`: es el unico escritor de esa clave (D53).
        this._roleService.clear();
        localStorage.removeItem('user-id');
        localStorage.removeItem('user-name');
        localStorage.removeItem('user-surnames');
        localStorage.setItem('authenticated', 'false');
        localStorage.removeItem('user-email');
        localStorage.removeItem('accessToken');
        localStorage.removeItem('tkExpires');
        localStorage.removeItem('user-avatar');
        localStorage.removeItem('tkRefresh');

        // Set the authenticated flag to false
        this._authenticated = false;

        // Return the observable
        return of(true);
    }

    /**
     * Check the authentication status
     */
    check(): Observable<boolean>
    {
        // Check if the user is logged in
        if (this._authenticated) {
            return of(true);
        }

        // Check localStorage flag
        if (!localStorage.getItem('authenticated') || localStorage.getItem('authenticated') === 'false') {
            return of(false);
        }

        // Check the access token availability
        if (!this.accessToken) {
            return of(false);
        }

        // Check the access token expire date
        if (AuthUtils.isTokenExpired(this.accessToken)) {
            return this.signInUsingToken();
        }

        // Token exists and is valid
        this._authenticated = true;
        return of(true);
    }

    /**
     * Get client credentials token (for public endpoints)
     */
    getClientCredentials(): Observable<any>
    {
        const body = {
            client_id: '1',
            grant_type: 'client_credentials',
            client_secret: environment.cs,
        };

        return this._httpClient.post(environment.urlBase + 'oauth/token', body);
    }

    /**
     * Forgot password
     *
     * @param email
     */
    forgotPassword(email: string): Observable<any>
    {
        return this._httpClient.post('api/auth/forgot-password', email);
    }

    /**
     * Reset password
     *
     * @param password
     */
    resetPassword(password: string): Observable<any>
    {
        return this._httpClient.post('api/auth/reset-password', password);
    }

    /**
     * Sign up
     *
     * @param user
     */
    signUp(user: { name: string; email: string; password: string; company: string }): Observable<any>
    {
        return this._httpClient.post('api/auth/sign-up', user);
    }

    /**
     * Unlock session
     *
     * @param credentials
     */
    unlockSession(credentials: { email: string; password: string }): Observable<any>
    {
        return this._httpClient.post('api/auth/unlock-session', credentials);
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Private methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Manage token storage
     */
    private manageToken(data: any): void
    {
        const now = new Date();
        const expiration = now.setSeconds(now.getSeconds() + data.expires_in);
        localStorage.setItem('accessToken', data.access_token);
        localStorage.setItem('tkExpires', expiration.toString());
        if (data.refresh_token) {
            localStorage.setItem('tkRefresh', data.refresh_token);
        }
        localStorage.setItem('authenticated', 'true');
    }
}
